// A Canvas2D adapter for the three batch sinks (R548).
//
// The KEYS legend draws its symbol swatches in the DOM, on a small 2D
// canvas, and it must draw the SAME glyph the map draws. `chart-glyphs.ts`
// emits into the sink interfaces of `fast/layers/batches.ts`, and this class
// implements those three interfaces over a Canvas2D context. The legend then
// calls the glyph leaf directly, and the map piece and the legend row can
// never fall out of step.
//
// **IT IS A DRAWING ADAPTER, NOT A BATCH.** `begin` clears nothing and
// `flush` uploads nothing: every call paints at once. `globalAlpha` is
// honoured as a multiplier on every alpha, which is what the batches do with
// it on flush. The engine's instance `flags` are a GPU concern and are
// ignored here; a legend swatch is drawn in the frame it is given.
//
// **A QUAD IS A RECTANGLE.** `quad` paints a filled, unrotated rectangle,
// and `sprite` paints the box the entry states in the same way. The legend
// draws vector glyphs, not atlas marks, so a sprite is a placeholder that
// says where the mark would stand.

import type { CircleSink, QuadSink, SegmentSink, SpriteEntry } from '../fast/layers/batches';
import type { CameraUniforms } from '../fast/layer';
import { hex } from './color';
import { clamp01 } from './math';

/**
 * The part of `CanvasRenderingContext2D` this adapter uses. It is stated as
 * its own interface so a test can hand in a recorder and jsdom needs no 2D
 * context at all.
 */
export interface Ctx2D {
  save(): void;
  restore(): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  stroke(): void;
  fill(): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  setLineDash(pattern: number[]): void;
  lineWidth: number;
  strokeStyle: string;
  fillStyle: string;
}

/** How many straight pieces one dashed ring becomes when it is stroked. */
const DASH_STEPS = 64;

/**
 * One Canvas2D context, wearing all three sink interfaces.
 *
 * A caller passes the same object as `seg`, `circ` and `quads`, because a
 * glyph asks for whichever of the three it needs and they all paint into
 * the one context.
 */
export class CanvasSink implements SegmentSink, CircleSink, QuadSink {
  /** A fade over everything drawn, exactly as a batch applies it. */
  globalAlpha = 1;

  /** The band scale of the chart steps: a GPU uniform, ignored here (R1796). */
  readonly stepScale = new Float32Array([1, 1, 1, 1]);

  /** How many calls this sink has painted. `begin` puts it back to 0. */
  count = 0;

  constructor(private readonly ctx: Ctx2D) {}

  begin(): void { this.count = 0; }

  /** Nothing is buffered, so a flush only answers what was painted. */
  flush(_cam?: CameraUniforms): number { return this.count; }

  line(
    x0: number, y0: number, x1: number, y1: number,
    width: number, color: number, alpha: number,
    _flags = 0, dashOn = 0, dashOff = 0,
  ): void {
    const c = this.ctx;
    c.save();
    if (dashOn > 0) c.setLineDash([dashOn, dashOff]);
    c.beginPath();
    c.moveTo(x0, y0);
    c.lineTo(x1, y1);
    c.lineWidth = width;
    c.strokeStyle = this.ink(color, alpha);
    c.stroke();
    c.restore();
    this.count++;
  }

  ring(
    x: number, y: number, r: number, width: number,
    color: number, alpha: number, _flags = 0,
  ): void {
    this.strokeArc(x, y, r, width, 0, Math.PI * 2, color, alpha);
  }

  disc(x: number, y: number, r: number, color: number, alpha: number, _flags = 0): void {
    const c = this.ctx;
    c.save();
    c.beginPath();
    c.arc(x, y, Math.max(0, r), 0, Math.PI * 2);
    c.fillStyle = this.ink(color, alpha);
    c.fill();
    c.restore();
    this.count++;
  }

  arc(
    x: number, y: number, r: number, width: number,
    start: number, sweep: number, color: number, alpha: number, _flags = 0,
  ): void {
    this.strokeArc(x, y, r, width, start, sweep, color, alpha);
  }

  /**
   * A dashed ring: `count` dashes round the sweep, each covering `fill` of
   * its own step. The batch draws it in the shader; here it is one stroked
   * arc per dash, which is the same picture at a legend's size.
   */
  dashed(
    x: number, y: number, r: number, width: number,
    count: number, fill: number, color: number, alpha: number,
    start = 0, sweep = Math.PI * 2, _flags = 0,
  ): void {
    const dashes = Math.max(1, Math.min(DASH_STEPS, Math.round(count)));
    const step = sweep / dashes;
    const on = step * clamp01(fill);
    for (let k = 0; k < dashes; k++) {
      this.strokeArc(x, y, r, width, start + k * step, on, color, alpha);
    }
  }

  quad(
    x: number, y: number, ox: number, oy: number, w: number, h: number,
    _rot: number, _flags: number, color: number, alpha: number,
    _u0 = 0, _v0 = 0, _u1 = 1, _v1 = 1,
  ): void {
    const c = this.ctx;
    c.save();
    c.fillStyle = this.ink(color, alpha);
    c.fillRect(x + ox - w / 2, y + oy - h / 2, w, h);
    c.restore();
    this.count++;
  }

  sprite(
    x: number, y: number, _rot: number, entry: SpriteEntry,
    tint: number, alpha: number, scale = 1, _flags = 0, _centred = true,
  ): void {
    this.quad(x, y, entry.ox, entry.oy, entry.w * scale, entry.h * scale,
      0, 0, tint, alpha);
  }

  /** A glyph of a chart word: its box at its own place, as a sprite (R1796). */
  glyph(
    _px: number, _py: number, x: number, y: number, rot: number, entry: SpriteEntry,
    tint: number, alpha: number, scale: number, flags: number,
  ): void {
    this.sprite(x, y, rot, entry, tint, alpha, scale, flags);
  }

  /** `#rrggbb` with the alpha the batch would have applied, as a style. */
  private ink(color: number, alpha: number): string {
    const a = clamp01(alpha * this.globalAlpha);
    return `${hex(color)}${Math.round(a * 255).toString(16).padStart(2, '0')}`;
  }

  private strokeArc(
    x: number, y: number, r: number, width: number,
    start: number, sweep: number, color: number, alpha: number,
  ): void {
    const c = this.ctx;
    c.save();
    c.beginPath();
    c.arc(x, y, Math.max(0, r), start, start + sweep);
    c.lineWidth = width;
    c.strokeStyle = this.ink(color, alpha);
    c.stroke();
    c.restore();
    this.count++;
  }
}
